import * as React from "react";
import { Check, Shield, UserCheck, Users, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";

export interface RoleOption {
  label: string;
  value: string;
  description?: string;
}

export interface UserOption {
  label: string;
  value: string;
  email?: string;
  role?: string;
}

export interface ChecklistAccessSelectProps {
  roleOptions: RoleOption[];
  userOptions: UserOption[];
  selectedRoles: string[];
  selectedUsers: string[];
  onRolesChange: (selected: string[]) => void;
  onUsersChange: (selected: string[]) => void;
  className?: string;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  maxDisplay?: number;
}

export function ChecklistAccessSelect({
  roleOptions = [],
  userOptions = [],
  selectedRoles = [],
  selectedUsers = [],
  onRolesChange,
  onUsersChange,
  className,
  placeholder = "Configure access...",
  id,
  disabled = false,
  maxDisplay = 3,
}: ChecklistAccessSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [roleSearch, setRoleSearch] = React.useState('');
  const [userSearch, setUserSearch] = React.useState('');

  const handleRoleSelect = (value: string) => {
    const newSelected = selectedRoles.includes(value)
      ? selectedRoles.filter((item) => item !== value)
      : [...selectedRoles, value];
    onRolesChange(newSelected);
  };

  const handleUserSelect = (value: string) => {
    const newSelected = selectedUsers.includes(value)
      ? selectedUsers.filter((item) => item !== value)
      : [...selectedUsers, value];
    onUsersChange(newSelected);
  };

  const handleSelectAllRoles = () => {
    onRolesChange(roleOptions.map(option => option.value));
  };

  const handleClearAllRoles = () => {
    onRolesChange([]);
  };

  const handleSelectAllUsers = () => {
    onUsersChange(userOptions.map(option => option.value));
  };

  const handleClearAllUsers = () => {
    onUsersChange([]);
  };

  const handleRemoveRole = (value: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onRolesChange(selectedRoles.filter(item => item !== value));
  };

  const handleRemoveUser = (value: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onUsersChange(selectedUsers.filter(item => item !== value));
  };

  const getSelectedRoleLabels = () => {
    return selectedRoles.map(value => 
      roleOptions.find(option => option.value === value)?.label || value
    );
  };

  const getSelectedUserLabels = () => {
    return selectedUsers.map(value => 
      userOptions.find(option => option.value === value)?.label || value
    );
  };

  const selectedRoleLabels = getSelectedRoleLabels();
  const selectedUserLabels = getSelectedUserLabels();
  const allRolesSelected = selectedRoles.length === roleOptions.length;
  const allUsersSelected = selectedUsers.length === userOptions.length;
  const noRolesSelected = selectedRoles.length === 0;
  const noUsersSelected = selectedUsers.length === 0;
  const nothingSelected = noRolesSelected && noUsersSelected;
  const allSelected = allRolesSelected && allUsersSelected;

  const totalSelected = selectedRoles.length + selectedUsers.length;
  const allLabels = [...selectedRoleLabels, ...selectedUserLabels];

  // Filter options based on search
  const filteredRoleOptions = roleOptions.filter(option =>
    option.label.toLowerCase().includes(roleSearch.toLowerCase()) ||
    (option.description && option.description.toLowerCase().includes(roleSearch.toLowerCase()))
  );

  const filteredUserOptions = userOptions.filter(option =>
    option.label.toLowerCase().includes(userSearch.toLowerCase()) ||
    (option.email && option.email.toLowerCase().includes(userSearch.toLowerCase())) ||
    (option.role && option.role.toLowerCase().includes(userSearch.toLowerCase()))
  );

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            type="button"
            className={cn(
              "flex h-auto min-h-[40px] w-full items-start justify-between rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-primary disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer hover:bg-muted hover:border-border",
              nothingSelected ? "text-muted-foreground" : "",
              className
            )}
          >
            <div className="flex flex-1 flex-wrap gap-1 items-start pointer-events-none">
              {nothingSelected ? (
                <span className="text-muted-foreground py-1">{placeholder}</span>
              ) : allSelected ? (
                <div className="flex items-center gap-1 py-1">
                  <Shield className="w-4 h-4 text-primary" />
                  <span className="text-primary font-medium">All access (Open checklist)</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1 max-w-full">
                  {/* Show role badges */}
                  {selectedRoleLabels.slice(0, maxDisplay).map((label, index) => {
                    const value = selectedRoles[index];
                    return (
                      <Badge
                        key={`role-${value}`}
                        variant="secondary"
                        className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 text-xs py-1 px-2 max-w-[120px] truncate"
                      >
                        <Shield className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                        <span
                          onClick={(e) => handleRemoveRole(value, e)}
                          className="ml-1 hover:bg-primary/30 rounded-full p-0.5 flex-shrink-0 pointer-events-auto cursor-pointer"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRemoveRole(value, e as any);
                            }
                          }}
                        >
                          <X className="w-3 h-3" />
                        </span>
                      </Badge>
                    );
                  })}
                  
                  {/* Show user badges */}
                  {selectedUserLabels.slice(0, maxDisplay - selectedRoleLabels.length).map((label, index) => {
                    const value = selectedUsers[index];
                    return (
                      <Badge
                        key={`user-${value}`}
                        variant="secondary"
                        className="bg-accent/5 text-accent border-accent/20 hover:bg-accent/10 text-xs py-1 px-2 max-w-[120px] truncate"
                      >
                        <UserCheck className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                        <span
                          onClick={(e) => handleRemoveUser(value, e)}
                          className="ml-1 hover:bg-accent/30 rounded-full p-0.5 flex-shrink-0 pointer-events-auto cursor-pointer"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRemoveUser(value, e as any);
                            }
                          }}
                        >
                          <X className="w-3 h-3" />
                        </span>
                      </Badge>
                    );
                  })}
                  
                  {/* Show overflow count */}
                  {totalSelected > maxDisplay && (
                    <Badge variant="outline" className="text-xs py-1 px-2">
                      +{totalSelected - maxDisplay} more
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        
        <PopoverContent 
          className="w-[var(--radix-popover-trigger-width)] p-0 z-50" 
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <div>
            <div className="flex items-center border-b px-3 py-2 bg-muted/50">
              <Users className="w-4 h-4 mr-2 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">
                Checklist Access ({totalSelected} selected)
              </span>
            </div>
            
            <Tabs defaultValue="roles" className="w-full">
              <TabsList className="grid grid-cols-2 w-full rounded-none border-b">
                <TabsTrigger value="roles" className="rounded-none">
                  <Shield className="w-4 h-4 mr-1" />
                  Roles ({roleOptions.length})
                </TabsTrigger>
                <TabsTrigger value="users" className="rounded-none">
                  <UserCheck className="w-4 h-4 mr-1" />
                  Users ({userOptions.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="roles" className="mt-0">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
                  <span className="text-xs font-medium text-muted-foreground">
                    Role-based access
                  </span>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleSelectAllRoles}
                      disabled={allRolesSelected || disabled}
                    >
                      All
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleClearAllRoles}
                      disabled={noRolesSelected || disabled}
                    >
                      None
                    </Button>
                  </div>
                </div>

                {/* Search input for roles */}
                <div className="px-3 py-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      placeholder="Search roles..."
                      value={roleSearch}
                      onChange={(e) => setRoleSearch(e.target.value)}
                      className="pl-8 h-8 text-xs"
                    />
                  </div>
                </div>

                <div 
                  className="max-h-64 overflow-auto"
                  onWheel={(e) => {
                    // Allow wheel scrolling within this container
                    e.stopPropagation();
                  }}
                >
                  {filteredRoleOptions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      {roleSearch ? 'No roles match your search.' : 'No roles found.'}
                    </div>
                  ) : (
                    <div className="p-1">
                      {filteredRoleOptions.map((option) => {
                        const isSelected = selectedRoles.includes(option.value);
                        return (
                          <div
                            key={option.value}
                            onClick={() => handleRoleSelect(option.value)}
                            className={cn(
                              "flex items-center space-x-2 cursor-pointer p-2 rounded-md text-sm transition-colors",
                              isSelected
                                ? "bg-primary/10 text-primary font-medium hover:bg-primary/20"
                                : "text-foreground hover:bg-primary/10 hover:text-primary"
                            )}
                          >
                            <div className="flex h-4 w-4 items-center justify-center rounded border border-primary">
                              {isSelected && <Check className="h-3 w-3 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">{option.label}</div>
                              {option.description && (
                                <div className="text-xs text-muted-foreground">
                                  {option.description}
                                </div>
                              )}
                            </div>
                            <Shield className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="users" className="mt-0">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
                  <span className="text-xs font-medium text-muted-foreground">
                    User-specific access
                  </span>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleSelectAllUsers}
                      disabled={allUsersSelected || disabled}
                    >
                      All
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleClearAllUsers}
                      disabled={noUsersSelected || disabled}
                    >
                      None
                    </Button>
                  </div>
                </div>

                {/* Search input for users */}
                <div className="px-3 py-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-8 h-8 text-xs"
                    />
                  </div>
                </div>

                <div 
                  className="max-h-64 overflow-auto"
                  onWheel={(e) => {
                    // Allow wheel scrolling within this container
                    e.stopPropagation();
                  }}
                >
                  {filteredUserOptions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      {userSearch ? 'No users match your search.' : 'No users found.'}
                    </div>
                  ) : (
                    <div className="p-1">
                      {filteredUserOptions.map((option) => {
                        const isSelected = selectedUsers.includes(option.value);
                        return (
                          <div
                            key={option.value}
                            onClick={() => handleUserSelect(option.value)}
                            className={cn(
                              "flex items-center space-x-2 cursor-pointer p-2 rounded-md text-sm transition-colors",
                              isSelected
                                ? "bg-accent/5 text-accent font-medium hover:bg-accent/10"
                                : "text-foreground hover:bg-primary/10 hover:text-primary"
                            )}
                          >
                            <div className="flex h-4 w-4 items-center justify-center rounded border border-primary">
                              {isSelected && <Check className="h-3 w-3 text-primary" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-muted-foreground">
                                {option.email} {option.role && `• ${option.role}`}
                              </div>
                            </div>
                            <UserCheck className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
} 